<template>
  <div class="challenge-mode" :class="{ 'is-correct': isCorrect, 'is-active': target !== null }">
    <!-- Idle state -->
    <div v-if="target === null" class="challenge-idle">
      <button class="challenge-start" type="button" @click="startChallenge">
        <svg class="challenge-start__glyph" viewBox="0 0 22 14" aria-hidden="true">
          <!-- Maya numeral: bar (5) with two dots (2) — reads as "7" -->
          <circle cx="6" cy="3" r="1.6" fill="currentColor"/>
          <circle cx="16" cy="3" r="1.6" fill="currentColor"/>
          <rect x="3" y="9" width="16" height="2.4" rx="0.4" fill="currentColor"/>
        </svg>
        <span class="challenge-start__label">{{ t.start }}</span>
      </button>
    </div>

    <!-- Active challenge -->
    <div v-else class="challenge-panel">
      <p class="challenge-prompt">{{ t.compose }}</p>
      <p class="challenge-target" :class="{ 'is-correct': isCorrect }">{{ target }}</p>

      <!-- Success state -->
      <div v-if="isCorrect" class="challenge-feedback challenge-feedback--success">
        <p class="challenge-correct">{{ t.correct }}</p>
        <div class="challenge-actions">
          <button class="challenge-link" type="button" @click="nextChallenge">{{ t.next }}</button>
          <span class="challenge-link__sep">·</span>
          <button class="challenge-link" type="button" @click="reset">{{ t.stop }}</button>
        </div>
      </div>

      <!-- Composing feedback -->
      <div v-else class="challenge-feedback">
        <p class="challenge-current">
          <span class="challenge-current__label">{{ t.current }}</span>
          <span class="challenge-current__value">{{ current }}</span>
        </p>
        <p class="challenge-diff" :class="diffSeverityClass">
          <span class="challenge-diff__label">{{ diffLabel }}</span>
          <span class="challenge-diff__value">{{ diff }}</span>
        </p>
        <div class="challenge-actions">
          <button class="challenge-link" type="button" @click="nextChallenge">{{ t.next }}</button>
          <span class="challenge-link__sep">·</span>
          <button class="challenge-link" type="button" @click="reset">{{ t.stop }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

const STRINGS = {
  es: {
    start: 'Modo reto',
    compose: 'Componga este número',
    current: 'Actual',
    higher: 'Faltan',
    lower: 'Sobran',
    correct: '✓ Correcto',
    next: 'Nuevo número',
    stop: 'Cerrar'
  },
  en: {
    start: 'Challenge mode',
    compose: 'Compose this number',
    current: 'Current',
    higher: 'Missing',
    lower: 'Excess',
    correct: '✓ Correct',
    next: 'New number',
    stop: 'Stop'
  }
}

export default {
  name: 'challenge-mode',
  data () {
    return {
      target: null,
      lang: this.detectLang()
    }
  },
  computed: {
    ...mapState({
      current (state) {
        return state.abacus.value
      }
    }),
    t () {
      return STRINGS[this.lang] || STRINGS.es
    },
    diff () {
      return Math.abs(this.target - this.current)
    },
    isCorrect () {
      return this.target !== null && this.current === this.target
    },
    diffLabel () {
      return this.current < this.target ? this.t.higher : this.t.lower
    },
    diffSeverityClass () {
      if (this.diff === 0) return 'diff--zero'
      if (this.diff <= 20) return 'diff--near'
      return 'diff--far'
    }
  },
  methods: {
    randomNumber () {
      return Math.floor(Math.random() * 9999) + 1
    },
    startChallenge () {
      this.target = this.randomNumber()
    },
    nextChallenge () {
      let n = this.randomNumber()
      while (n === this.target) n = this.randomNumber()
      this.target = n
    },
    reset () {
      this.target = null
    },
    detectLang () {
      try {
        const stored = localStorage.getItem('nepo:lang')
        if (stored === 'en' || stored === 'es') return stored
      } catch (e) { /* ignore */ }
      return 'es'
    }
  }
}
</script>

<style scoped>
.challenge-mode {
  max-width: 560px;
  margin: 2.5rem auto 1.5rem;
  padding: 0 1rem;
  transition: opacity 220ms ease;
}

/* ---------- Idle: the start button ---------- */
.challenge-idle {
  display: flex;
  justify-content: center;
}

.challenge-start {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.85rem 1.6rem;
  background: var(--cinabrio);
  color: var(--amate);
  border: none;
  border-radius: 2px;
  cursor: pointer;
  font-family: 'Cinzel', 'Cinzel Decorative', serif;
  font-variant: small-caps;
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  line-height: 1;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08), 0 6px 14px -8px rgba(122, 24, 23, 0.5);
  transition: background-color 200ms ease, transform 200ms ease, box-shadow 200ms ease;
}

.challenge-start:hover,
.challenge-start:focus-visible {
  background: var(--cinabrio-deep);
  outline: none;
  transform: translateY(-1px);
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.1), 0 10px 18px -8px rgba(122, 24, 23, 0.6);
}

.challenge-start:active {
  transform: translateY(0);
}

.challenge-start__glyph {
  width: 22px;
  height: 14px;
  color: var(--amate);
  opacity: 0.95;
  flex-shrink: 0;
}

.challenge-start__label {
  display: inline-block;
}

/* ---------- Active panel ---------- */
.challenge-panel {
  border: 1px solid var(--ink-trace);
  background: var(--amate-warm);
  padding: 1.5rem 1.5rem 1.25rem;
  text-align: center;
  border-radius: 2px;
  transition: background-color 280ms ease, border-color 280ms ease, box-shadow 280ms ease;
}

.challenge-mode.is-correct .challenge-panel {
  border-color: var(--jade);
  box-shadow: 0 0 0 1px var(--jade) inset, 0 0 24px -8px var(--jade);
}

.challenge-prompt {
  font-family: 'Cinzel', serif;
  font-variant: small-caps;
  font-size: 0.85rem;
  letter-spacing: 0.14em;
  color: var(--obsidiana-soft);
  margin: 0 0 0.6rem;
}

.challenge-target {
  font-family: 'DM Mono', monospace;
  font-size: clamp(2.4rem, 6vw, 3rem);
  font-weight: 500;
  line-height: 1;
  color: var(--cinabrio);
  margin: 0;
  letter-spacing: 0.02em;
  transition: color 240ms ease;
}

.challenge-target.is-correct {
  color: var(--jade-deep);
}

/* ---------- Feedback ---------- */
.challenge-feedback {
  margin-top: 1.2rem;
}

.challenge-current,
.challenge-diff {
  margin: 0.35rem 0;
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 1rem;
  color: var(--obsidiana-soft);
  display: inline-flex;
  align-items: baseline;
  gap: 0.55rem;
}

.challenge-current {
  display: flex;
  justify-content: center;
}

.challenge-diff {
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
}

.challenge-current__label,
.challenge-diff__label {
  font-style: italic;
  font-size: 0.95rem;
}

.challenge-current__value,
.challenge-diff__value {
  font-family: 'DM Mono', monospace;
  font-size: 1.1rem;
  font-style: normal;
  color: var(--obsidiana);
}

.diff--zero .challenge-diff__value { color: var(--jade-deep); }
.diff--near .challenge-diff__value,
.diff--near .challenge-diff__label { color: var(--maíz-deep); }
.diff--far .challenge-diff__value,
.diff--far .challenge-diff__label { color: var(--obsidiana-soft); }

/* ---------- Success message ---------- */
.challenge-feedback--success {
  margin-top: 1.1rem;
}

.challenge-correct {
  display: inline-block;
  margin: 0.4rem 0 0.9rem;
  padding: 0.45rem 1.1rem;
  background: var(--jade);
  color: var(--amate);
  font-family: 'Cinzel', serif;
  font-variant: small-caps;
  letter-spacing: 0.1em;
  font-size: 0.95rem;
  border-radius: 2px;
  box-shadow: 0 0 16px -6px var(--jade);
}

/* ---------- Link-style actions ---------- */
.challenge-actions {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 0.6rem;
}

.challenge-link {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'Crimson Pro', Georgia, serif;
  font-style: italic;
  font-size: 0.98rem;
  color: var(--cinabrio);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  transition: color 180ms ease;
}

.challenge-link:hover,
.challenge-link:focus-visible {
  color: var(--cinabrio-deep);
  outline: none;
}

.challenge-link__sep {
  color: var(--ink-trace);
  font-family: 'Crimson Pro', serif;
  font-size: 1rem;
  line-height: 1;
}
</style>



// WEBPACK FOOTER //
// ChallengeMode.vue?3bade261